import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Avatar,
  InputAdornment,
  Alert,
  Snackbar,
} from "@mui/material";

// Icons
import StorefrontIcon from "@mui/icons-material/Storefront";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import BadgeIcon from "@mui/icons-material/Badge";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import LanguageIcon from "@mui/icons-material/Language";

import MuiPageHeader from "../../components/mui/MuiPageHeader";
import { postProvider } from "../../helpers/api_helper";
import VATModal from "../Customers/vatModal";
import MapModal from "../Customers/mapAddress";

const MuiProviderCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  document.title = t("Create Provider") + " | FlexFlow ERP";

  // Form state
  const [startDate, setStartDate] = useState(new Date());
  const [name, setName] = useState("");
  const [label, setLabel] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [vat, setVat] = useState("");
  const [taxOffice, setTaxOffice] = useState("");
  const [representor, setRepresentor] = useState("");
  const [profession, setProfession] = useState("");
  const [social, setSocial] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [vatType, setVatType] = useState("24");
  const [discount, setDiscount] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Format date for input
  const formatDateForInput = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  const setCompanyDetails = (compDetails) => {
    if (compDetails) {
      setVat(compDetails.basicInfo.afm);
      setName(compDetails.basicInfo.onomasia);
      setTaxOffice(compDetails.basicInfo.doy_descr);
      setCountry("Greece");
      setProfession(compDetails.firmActivities?.[0]?.firm_act_descr || "");
      setCity(compDetails.basicInfo.postal_area_description);
      setAddress(
        `${compDetails.basicInfo.postal_address}, ${compDetails.basicInfo.postal_address_no}, ${compDetails.basicInfo.postal_zip_code}`
      );
    }
  };

  const createProvider = async () => {
    setIsSubmitting(true);
    setError(null);

    const payload = {
      resource: [
        {
          name: name,
          label: label,
          taxOffice: taxOffice,
          address: address,
          phone: phone,
          startdate: startDate,
          representor: representor,
          discount: discount,
          vatType: vatType,
          vat: vat,
          social: social,
          profession: profession,
          email: email,
          tags: JSON.stringify([]),
          salespersons: JSON.stringify([]),
          city: city,
          country: country,
        },
      ],
    };

    try {
      const res = await postProvider(payload);
      if (res?.error) {
        setError(res.error.message || t("Failed to create provider"));
        setSnackbar({ open: true, message: t("Failed to create provider"), severity: "error" });
        setIsSubmitting(false);
      } else if (res?.resource) {
        setSnackbar({ open: true, message: t("Provider created successfully"), severity: "success" });
        setTimeout(() => navigate("/providers"), 1000);
      }
    } catch (err) {
      setError(t("Failed to create provider"));
      setSnackbar({ open: true, message: t("Failed to create provider"), severity: "error" });
      setIsSubmitting(false);
    }
  };

  const breadcrumbs = [
    { label: t("Providers"), path: "/providers" },
    { label: t("Create Provider") },
  ];

  const isFormValid = name.length >= 4;

  // Section Card Component
  const SectionCard = ({ icon: Icon, title, children, iconColor = "#8b5cf6", iconBg = "#f3e8ff" }) => (
    <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", mb: 3 }}>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f3f4f6", bgcolor: "#fafafa" }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: iconBg, color: iconColor }}>
              <Icon sx={{ fontSize: 20 }} />
            </Avatar>
            <Typography variant="subtitle1" fontWeight={600} color="#111827">
              {title}
            </Typography>
          </Stack>
        </Box>
        <Box sx={{ p: 3 }}>{children}</Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ py: 3, bgcolor: "#f9fafb", minHeight: "100vh" }}>
      <Container maxWidth="lg">
        <MuiPageHeader
          title={t("Create Provider")}
          subtitle={t("Add a new supplier or vendor to your database")}
          breadcrumbs={breadcrumbs}
          actions={
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/providers")}
              sx={{ borderRadius: 2 }}
            >
              {t("Back to List")}
            </Button>
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left Column - Main Form */}
          <Grid item xs={12} lg={8}>
            {/* VAT Lookup Section */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "2px dashed #d1d5db",
                mb: 3,
                bgcolor: "#faf5ff",
                transition: "all 0.2s ease",
                "&:hover": { borderColor: "#8b5cf6", bgcolor: "#f3e8ff" },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ width: 48, height: 48, bgcolor: "#8b5cf6" }}>
                    <SearchIcon />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} color="#111827">
                      {t("Quick Fill with VAT Number")}
                    </Typography>
                    <Typography variant="body2" color="#6b7280">
                      {t("Enter a VAT number to automatically fill company details from AADE")}
                    </Typography>
                  </Box>
                  <VATModal ValidData={setCompanyDetails} />
                </Stack>
              </CardContent>
            </Card>

            {/* Basic Information */}
            <SectionCard icon={StorefrontIcon} title={t("Provider Information")}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("Provider Name")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("Enter provider name")}
                    required
                    error={name.length > 0 && name.length < 4}
                    helperText={name.length > 0 && name.length < 4 ? t("Name must be at least 4 characters") : ""}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <StorefrontIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        "&.Mui-focused": {
                          boxShadow: "0 0 0 3px rgba(139, 92, 246, 0.1)",
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("Company Label")}
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder={t("Enter company label")}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("Contact Person")}
                    value={representor}
                    onChange={(e) => setRepresentor(e.target.value)}
                    placeholder={t("Enter contact person name")}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <BadgeIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("Profession")}
                    value={profession}
                    onChange={(e) => setProfession(e.target.value)}
                    placeholder={t("Enter profession")}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <WorkOutlineIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* Contact Information */}
            <SectionCard icon={PhoneIcon} title={t("Contact Details")} iconColor="#10b981" iconBg="#d1fae5">
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("Phone")}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder={t("Enter phone number")}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("Email")}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("Enter email address")}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("Social / Website")}
                    value={social}
                    onChange={(e) => setSocial(e.target.value)}
                    placeholder={t("Enter website or social media")}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LanguageIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* Address Information */}
            <SectionCard icon={LocationOnIcon} title={t("Address")} iconColor="#f59e0b" iconBg="#fef3c7">
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <TextField
                      fullWidth
                      label={t("Street Address")}
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder={t("Enter street address")}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LocationOnIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                    <MapModal address={address} onAddressSelect={(a) => setAddress(a)} />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("City")}
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder={t("Enter city")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("Country")}
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    placeholder={t("Enter country")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* Tax Information */}
            <SectionCard icon={ReceiptLongIcon} title={t("Tax Information")} iconColor="#6366f1" iconBg="#eef2ff">
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("VAT Number")}
                    value={vat}
                    onChange={(e) => setVat(e.target.value)}
                    placeholder={t("Enter VAT number")}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ReceiptLongIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("Tax Office (DOY)")}
                    value={taxOffice}
                    onChange={(e) => setTaxOffice(e.target.value)}
                    placeholder={t("Enter tax office")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t("VAT Type")}</InputLabel>
                    <Select
                      value={vatType}
                      label={t("VAT Type")}
                      onChange={(e) => setVatType(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="0">0%</MenuItem>
                      <MenuItem value="17">17%</MenuItem>
                      <MenuItem value="24">24%</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t("Default Discount")}
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                    placeholder={t("Enter discount %")}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            </SectionCard>
          </Grid>

          {/* Right Column - Preview & Actions */}
          <Grid item xs={12} lg={4}>
            {/* Provider Preview Card */}
            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", mb: 3, position: "sticky", top: 24 }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f3f4f6", bgcolor: "#fafafa" }}>
                  <Typography variant="subtitle2" fontWeight={600} color="#374151">
                    {t("Provider Preview")}
                  </Typography>
                </Box>

                <Box sx={{ p: 3 }}>
                  {/* Avatar & Name */}
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: name ? "#8b5cf6" : "#e5e7eb",
                        fontSize: "1.25rem",
                        fontWeight: 600,
                      }}
                    >
                      {name ? name.charAt(0).toUpperCase() : <StorefrontIcon />}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600} color={name ? "#111827" : "#9ca3af"}>
                        {name || t("Provider Name")}
                      </Typography>
                      <Typography variant="body2" color="#6b7280">
                        {label || t("Company Label")}
                      </Typography>
                    </Box>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  {/* Details */}
                  <Stack spacing={1.5}>
                    {representor && (
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <BadgeIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                        <Typography variant="body2" color="#374151">{representor}</Typography>
                      </Stack>
                    )}
                    {email && (
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <EmailIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                        <Typography variant="body2" color="#374151">{email}</Typography>
                      </Stack>
                    )}
                    {phone && (
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <PhoneIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                        <Typography variant="body2" color="#374151">{phone}</Typography>
                      </Stack>
                    )}
                    {address && (
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <LocationOnIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                        <Typography variant="body2" color="#374151" sx={{ wordBreak: "break-word" }}>
                          {address}
                        </Typography>
                      </Stack>
                    )}
                    {vat && (
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <ReceiptLongIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                        <Typography variant="body2" color="#374151">{t("VAT")}: {vat}</Typography>
                      </Stack>
                    )}
                  </Stack>

                  {/* Profession Badge */}
                  {profession && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Box
                        sx={{
                          display: "inline-block",
                          px: 1.5,
                          py: 0.5,
                          bgcolor: "#f3e8ff",
                          color: "#8b5cf6",
                          borderRadius: 1.5,
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        {profession}
                      </Box>
                    </>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Additional Settings Card */}
            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", mb: 3 }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f3f4f6", bgcolor: "#fafafa" }}>
                  <Typography variant="subtitle2" fontWeight={600} color="#374151">
                    {t("Additional Settings")}
                  </Typography>
                </Box>

                <Box sx={{ p: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label={t("Creation Date")}
                    value={formatDateForInput(startDate)}
                    onChange={(e) => setStartDate(new Date(e.target.value))}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarMonthIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Box>
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <Stack spacing={2}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={<SaveIcon />}
                onClick={createProvider}
                disabled={!isFormValid || isSubmitting}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: "#8b5cf6",
                  boxShadow: "0 4px 14px rgba(139, 92, 246, 0.3)",
                  "&:hover": {
                    bgcolor: "#7c3aed",
                    boxShadow: "0 6px 20px rgba(139, 92, 246, 0.4)",
                  },
                }}
              >
                {isSubmitting ? t("Creating...") : t("Create Provider")}
              </Button>

              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={<CloseIcon />}
                onClick={() => navigate("/providers")}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 500,
                  borderColor: "#d1d5db",
                  color: "#6b7280",
                  "&:hover": {
                    borderColor: "#9ca3af",
                    bgcolor: "#f9fafb",
                  },
                }}
              >
                {t("Cancel")}
              </Button>
            </Stack>

            {/* Help Text */}
            <Box sx={{ mt: 3, p: 2, bgcolor: "#f9fafb", borderRadius: 2, border: "1px solid #e5e7eb" }}>
              <Typography variant="caption" color="#6b7280" sx={{ display: "block", lineHeight: 1.6 }}>
                <strong>{t("Tip")}:</strong> {t("Use the VAT lookup feature to automatically fill in company details from the Greek tax authority (AADE).")}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MuiProviderCreate;
